import { useState } from "react";
import { CheckmarkFilled, UserFollow } from "@carbon/icons-react";
import { Tile, Button, Select, SelectItem } from "@carbon/react";
import { useTranslation } from "react-i18next";
import OffersReceivedNotValidated from "../OffersReceivedNotValidated/OffersReceivedNotValidated";
import OffersReceivedValidated from "../OffersReceivedValidated/OffersReceivedValidated";
import PotencialSuppliers from "../PotencialSuppliers/PotencialSuppliers";
import Pagination from "../../UI/Pagination/Pagination";
import { useLanguage } from "../../../hooks";
import { Loader } from "../..";
import {
  CombinedBuyerSellerMaterialOfferResponse,
  CountryResponse,
} from "../../../types";
import { useNewMaterialCompanyOpportunitiesByCountry } from "../../../hooks/useMaterialOpportunities";
import "./suppliers.scss";

type SuppliersProps = {
  materialId: string;
  countryId: string;
  onClick: () => void;
  isActive: boolean;
  isOwner: boolean;
  materialOffers: CombinedBuyerSellerMaterialOfferResponse[];
  setOpen: (b: boolean) => void;
  setMaterialOfferToShow: (b: CombinedBuyerSellerMaterialOfferResponse) => void;
  setPotencialSupplierToShow: (b: boolean) => void;
  setOpenChat: (b: boolean) => void;
  setOpenSupplier: (b: boolean) => void;
  countriesByMaterial?: CountryResponse[];
};

/**
 * The material detail page's suppliers panel — rebuilt on Carbon (this pass) to match the Detail
 * and AI-market-info `Tile`s beside it: the pre-Carbon `bg-white rounded-md shadow` card, its
 * `text-xxs uppercase` section headers, a raw circular invite `<button>` and a native `<select>`
 * are now a Carbon `Tile` with `label-01` section titles, a ghost icon `Button` (its own tooltip
 * replaces the bespoke `TooltipSP`) and a Carbon `Select`.
 *
 * The three list children (`OffersReceivedValidated` / `OffersReceivedNotValidated` /
 * `PotencialSuppliers`) render exactly the same offers/opportunities as before — only their empty
 * states and list chrome moved onto Carbon tokens. The offer ROW itself (`MaterialOfferItem`) and
 * every slide-over opened from here are out of this pass's scope, carried over untouched.
 */
export default function Suppliers({
  materialId,
  countryId,
  setOpen,
  onClick,
  isActive,
  isOwner,
  setOpenChat,
  materialOffers,
  setOpenSupplier,
  countriesByMaterial,
  setMaterialOfferToShow,
  setPotencialSupplierToShow,
}: SuppliersProps) {
  const [selectedCountry, setSelectedCountry] = useState(countryId);
  const [page, setPage] = useState(0);
  const { t } = useTranslation();
  const langEs = useLanguage();

  const {
    data: materialCompaniesOpportunitiesByCountry,
    isLoading: isLoadingMaterialOpportunities,
  } = useNewMaterialCompanyOpportunitiesByCountry(
    materialId,
    selectedCountry,
    page
  );

  return (
    <Tile className="material-suppliers">
      <section className="material-suppliers__section">
        <div className="material-suppliers__section-head">
          <h2 className="material-suppliers__section-title">
            <span>{t("material.validatedSuppliers")}</span>
            <CheckmarkFilled
              size={16}
              className="material-suppliers__validated-icon"
              aria-hidden="true"
            />
          </h2>
          {isActive && isOwner && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={UserFollow}
              iconDescription={t("invite.invite")}
              tooltipPosition="left"
              onClick={onClick}
            />
          )}
        </div>
        <OffersReceivedValidated
          materialOffers={materialOffers}
          setOpen={setOpen}
          setMaterialOfferToShow={setMaterialOfferToShow}
          setOpenChat={setOpenChat}
        />
      </section>

      <section className="material-suppliers__section">
        <div className="material-suppliers__section-head">
          <h2 className="material-suppliers__section-title">
            <span>{t("material.alternativeSuppliers")}</span>
          </h2>
        </div>
        <OffersReceivedNotValidated
          materialOffers={materialOffers}
          setOpen={setOpen}
          setMaterialOfferToShow={setMaterialOfferToShow}
          setOpenChat={setOpenChat}
        />
      </section>

      <section className="material-suppliers__section">
        <div className="material-suppliers__section-head">
          <h2 className="material-suppliers__section-title">
            <span>{t("material.potencialSuppliers")}</span>
          </h2>
          {countriesByMaterial && countriesByMaterial.length > 0 && (
            <Select
              id="material-suppliers-country"
              className="material-suppliers__country"
              size="sm"
              labelText={t("material.country")}
              hideLabel
              defaultValue={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              {countriesByMaterial.map((country) => (
                <SelectItem
                  key={country.id}
                  value={country.id}
                  text={langEs ? country.name : country.nameEn}
                />
              ))}
            </Select>
          )}
        </div>
        {isLoadingMaterialOpportunities ? (
          <Loader />
        ) : (
          <>
            <PotencialSuppliers
              setOpen={setOpenSupplier}
              materialCompaniesOpportunities={
                materialCompaniesOpportunitiesByCountry
              }
              setPotencialSupplierToShow={setPotencialSupplierToShow}
            />

            {materialCompaniesOpportunitiesByCountry?.totalElements > 10 && (
              <Pagination
                results={materialCompaniesOpportunitiesByCountry.numberOfElements}
                total={materialCompaniesOpportunitiesByCountry.totalElements}
                amountOfPages={materialCompaniesOpportunitiesByCountry.totalPages}
                currentPage={page}
                setPageNumber={setPage}
              />
            )}
          </>
        )}
      </section>
    </Tile>
  );
}
